/**
 * RuleSetActions — the Rules tab's library controls: save the current rule
 * trees + capacity rules as ONE set (fork-aware) and load one back in a
 * single draft update (fresh ids for capacity + rule groups).
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC, useState } from 'react';
import type {
  CapacityRule,
  ExcludeRulesTree,
  FlowOptimizerRuleSet,
} from '@trafficpulse/shared';

import { ConfirmDialog } from '@/components/ui/layout/ConfirmDialog';

import { applyRuleSetPayload } from '../envelope/apply-rule-set';
import type { AppliedRuleSet } from '../envelope/apply-rule-set';
import {
  EditForkDialog,
  SaveSetButton,
  SaveSetDialog,
  useSaveSet,
} from '../save';
import SetPickerMenu from './SetPickerMenu';

export interface RuleSetActionsProps {
  excludeRules: ExcludeRulesTree;
  capacityRules: CapacityRule[];
  /** One call carries BOTH halves — a single draft update, no update race. */
  onApply: (applied: AppliedRuleSet) => void;
  packageId?: string | null;
}

const RuleSetActions: FC<RuleSetActionsProps> = ({
  excludeRules,
  capacityRules,
  onApply,
  packageId,
}) => {
  const save = useSaveSet({
    kind: 'rule',
    buildPayload: () => ({ excludeRules, capacityRules }),
    packageId,
  });
  const [pendingLoad, setPendingLoad] = useState<FlowOptimizerRuleSet | null>(null);
  const hasWork =
    Boolean(excludeRules.paths || excludeRules.landers || excludeRules.offers) ||
    capacityRules.length > 0;

  const applySet = (set: FlowOptimizerRuleSet) => {
    onApply(
      applyRuleSetPayload({
        excludeRules: set.excludeRules,
        capacityRules: set.capacityRules,
      }),
    );
    setPendingLoad(null);
  };

  return (
    <>
      <SaveSetButton
        disabledReason={
          hasWork ? undefined : 'Add rules or capacity limits to save a set.'
        }
        onClick={() => save.setDialogOpen(true)}
      />
      <SetPickerMenu
        kind="rule"
        onPick={(set) => {
          const ruleSet = set as FlowOptimizerRuleSet;
          if (hasWork) setPendingLoad(ruleSet);
          else applySet(ruleSet);
        }}
      />
      <SaveSetDialog
        open={save.dialogOpen}
        kindLabel="rule"
        sets={save.sets}
        busy={save.saving}
        onSubmit={save.submit}
        onClose={() => save.setDialogOpen(false)}
      />
      <EditForkDialog
        open={save.forkPrompt !== null}
        setName={save.forkPrompt?.setName ?? ''}
        packages={save.forkPrompt?.packages ?? 0}
        campaigns={save.forkPrompt?.campaigns ?? 0}
        willRepoint={Boolean(packageId)}
        busy={save.saving}
        onUpdateAll={save.updateForEveryone}
        onSaveCopy={save.saveAsCopy}
        onCancel={save.cancelFork}
      />
      <ConfirmDialog
        open={pendingLoad !== null}
        title="Load set into the Rules tab?"
        description={
          pendingLoad
            ? `"${pendingLoad.name}" replaces the current rules AND capacity ` +
              'limits. Nothing is saved until you press Save Config.'
            : ''
        }
        confirmLabel="Load set"
        onConfirm={() => pendingLoad && applySet(pendingLoad)}
        onCancel={() => setPendingLoad(null)}
      />
    </>
  );
};

export default RuleSetActions;
